import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  Download,
  MessageCircle,
  Paperclip,
  Plus,
  Save,
  Send,
  Trash2,
  Upload,
  UserPlus,
  X,
} from "lucide-react";
import type { TaskParticipantPermission } from "../types/tasks";
import {
  formatTaskDate,
  formatTaskDateTime,
  formatTaskFileSize,
  formatTaskMinutes,
  getTaskActorCapabilities,
  getTaskPriorityLabel,
  getTaskStateLabel,
  type Task,
  type TaskAttachment,
  type TaskMeta,
  type TaskState,
  type TaskTimeEntry,
} from "../types/tasks";

type Props = {
  open: boolean;
  task: Task | null;
  meta: TaskMeta;
  busy: boolean;
  onClose: () => void;
  onChangeState: (task: Task, state: TaskState) => Promise<void>;
  onAssign: (task: Task, userId: number) => Promise<void>;
  onAddParticipant: (
    task: Task,
    userId: number,
    permission: TaskParticipantPermission,
  ) => Promise<void>;
  onRemoveParticipant: (task: Task, userId: number) => Promise<void>;
  onCreateSubtask: (task: Task, title: string) => Promise<void>;
  onToggleSubtask: (task: Task, subtaskId: number) => Promise<void>;
  onDeleteSubtask: (task: Task, subtaskId: number) => Promise<void>;
  onSendComment: (task: Task, value: string) => Promise<void>;
  onUploadAttachments: (task: Task, files: File[]) => Promise<void>;
  onDownloadAttachment: (attachment: TaskAttachment) => Promise<void>;
  onDeleteAttachment: (attachment: TaskAttachment) => Promise<void>;
  onStartTime: (task: Task) => Promise<void>;
  onStopTime: (task: Task, entry: TaskTimeEntry) => Promise<void>;
  onDeleteTask: (task: Task) => Promise<void>;
};

export function TaskDetailDrawer({
  open,
  task,
  meta,
  busy,
  onClose,
  onChangeState,
  onAssign,
  onAddParticipant,
  onRemoveParticipant,
  onCreateSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onSendComment,
  onUploadAttachments,
  onDownloadAttachment,
  onDeleteAttachment,
  onStartTime,
  onStopTime,
  onDeleteTask,
}: Props) {
  const [comment, setComment] = useState("");
  const [subtaskTitle, setSubtaskTitle] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState("");
  const [selectedParticipantPermission, setSelectedParticipantPermission] =
    useState<TaskParticipantPermission>("lector");
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);

  useEffect(() => {
    setComment("");
    setSubtaskTitle("");
    setSelectedAssignee(task?.asignado_id ? String(task.asignado_id) : "");
    setSelectedParticipant("");
    setSelectedParticipantPermission("lector");
    setUploadFiles([]);
  }, [task?.id, task?.asignado_id]);

  const capabilities = useMemo(
    () =>
      task
        ? getTaskActorCapabilities(task, meta)
        : {
            isManager: false,
            canChangeState: false,
            canAssign: false,
            canManageParticipants: false,
            canManageSubtasks: false,
            canComment: false,
            canUploadAttachments: false,
            canDeleteAttachments: false,
            canTrackTime: false,
            canStartTime: false,
            canStopTime: false,
            canDeleteTask: false,
            nextStates: [],
            availableStates: [],
          },
    [meta, task],
  );

  useEffect(() => {
    if (!capabilities.canAssign && selectedParticipantPermission === "responsable") {
      setSelectedParticipantPermission("lector");
    }
  }, [capabilities.canAssign, selectedParticipantPermission]);

  return (
    <>
      <div
        className={[
          "fixed inset-0 z-40 bg-white/35 backdrop-blur-[2px] transition-opacity duration-300",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
        onClick={onClose}
      />

      <div
        className={[
          "tasks-drawer fixed inset-y-0 right-0 z-50 w-full transform border-l border-white/60 bg-white/88 shadow-[0_44px_120px_rgba(15,23,42,.24)] backdrop-blur-2xl transition duration-300",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-full flex-col">
          <header className="border-b border-white/60 bg-white/[0.58] px-6 py-5 backdrop-blur-2xl">
            <div className="pointer-events-none absolute -right-24 top-[-120px] h-[280px] w-[280px] rounded-full bg-emerald-300/24 blur-3xl" />
            <div className="pointer-events-none absolute left-1/3 top-[-120px] h-[240px] w-[240px] rounded-full bg-teal-300/14 blur-3xl" />

            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50/80 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700 shadow-[0_8px_22px_rgba(16,185,129,.08)]">
                  Tarea #{task?.id ?? "—"}
                </div>

                <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                  {task?.titulo ?? "Detalle de tarea"}
                </h2>

                <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                  {task ? getTaskStateLabel(task.estado) : "Selecciona una tarea"}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500"
              >
                <X size={17} />
              </button>
            </div>

            {task && (
              <div className="relative mt-5 flex flex-wrap gap-2">
                {capabilities.availableStates.map((state) => (
                  <ActionButton
                    key={state}
                    icon={CheckCircle2}
                    text={getTaskStateActionLabel(state)}
                    busy={busy}
                    onClick={() => void onChangeState(task, state)}
                  />
                ))}

                {capabilities.canDeleteTask && (
                  <ActionButton
                    icon={Trash2}
                    text="Eliminar tarea"
                    tone="danger"
                    busy={busy}
                    onClick={() => {
                      if (!window.confirm("¿Eliminar esta tarea?")) return;
                      void onDeleteTask(task);
                    }}
                  />
                )}
              </div>
            )}
          </header>

          <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1.25fr)_360px]">
            <div className="tasks-detail-scroll space-y-5 overflow-y-auto px-6 py-5">
              <section className="rounded-[30px] border border-[#dfe8e4] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,.06)]">
                <h3 className="text-lg font-black text-slate-950">Resumen</h3>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <SummaryItem label="Proyecto" value={task?.proyecto_nombre ?? "Sin proyecto"} />
                  <SummaryItem label="Prioridad" value={task ? getTaskPriorityLabel(task.prioridad) : "—"} />
                  <SummaryItem label="Responsable" value={task?.asignado_nombre ?? "Sin asignar"} />
                  <SummaryItem label="Creada por" value={task?.creado_por_nombre ?? "—"} />
                  <SummaryItem label="Inicio" value={formatTaskDate(task?.fecha_inicio)} />
                  <SummaryItem label="Fin" value={formatTaskDate(task?.fecha_fin)} />
                  <SummaryItem
                    label="Subtareas"
                    value={
                      task
                        ? `${task.subtareas_completadas}/${task.subtareas_total}`
                        : "0/0"
                    }
                  />
                  <SummaryItem label="Tiempo total" value={formatTaskMinutes(task?.minutos_total)} />
                </div>

                <div className="mt-5 rounded-[24px] border border-[#edf1f3] bg-[#fbfdfc] p-4">
                  <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                    Descripción
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-7 text-slate-600">
                    {task?.descripcion?.trim() || "La tarea aún no tiene una descripción detallada."}
                  </p>
                </div>
              </section>

              {task && capabilities.canAssign && (
                <section className="rounded-[30px] border border-[#dfe8e4] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,.06)]">
                  <h3 className="text-lg font-black text-slate-950">Responsable</h3>
                  <div className="mt-4 flex flex-col gap-3 md:flex-row">
                    <select
                      value={selectedAssignee}
                      onChange={(event) => setSelectedAssignee(event.target.value)}
                      className="field-control"
                    >
                      <option value="">Selecciona responsable</option>
                      {meta.users.map((user) => (
                        <option key={user.id} value={String(user.id)}>
                          {user.nombre}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      disabled={busy || !selectedAssignee}
                      onClick={() => void onAssign(task, Number(selectedAssignee))}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_50%,#00a879_100%)] px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Save size={15} />
                      Guardar responsable
                    </button>
                  </div>
                </section>
              )}

              {task && (
                <section className="rounded-[30px] border border-[#dfe8e4] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,.06)]">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-black text-slate-950">Subtareas</h3>
                    <span className="rounded-full border border-[#dfe8e4] bg-[#fbfdfc] px-3 py-1 text-xs font-black text-slate-500">
                      {task.subtareas_completadas}/{task.subtareas_total}
                    </span>
                  </div>

                  {capabilities.canManageSubtasks && (
                    <div className="mt-4 flex flex-col gap-3 md:flex-row">
                      <input
                        value={subtaskTitle}
                        onChange={(event) => setSubtaskTitle(event.target.value)}
                        className="field-control"
                        placeholder="Añadir subtarea..."
                      />
                      <button
                        type="button"
                        disabled={busy || !subtaskTitle.trim()}
                        onClick={() => {
                          void onCreateSubtask(task, subtaskTitle.trim());
                          setSubtaskTitle("");
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#00d09c]/18 bg-[#00d09c]/8 px-5 py-3 text-sm font-black text-[#0b4a2f] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Plus size={15} />
                        Añadir
                      </button>
                    </div>
                  )}

                  <div className="mt-4 space-y-3">
                    {task.subtareas.length === 0 ? (
                      <EmptyPanel text="No hay subtareas todavía." />
                    ) : (
                      task.subtareas.map((subtask) => (
                        <div
                          key={subtask.id}
                          className="flex items-center justify-between gap-3 rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] px-4 py-3"
                        >
                          <button
                            type="button"
                            onClick={() => void onToggleSubtask(task, subtask.id)}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          >
                            <span
                              className={[
                                "grid h-6 w-6 place-items-center rounded-full border text-xs font-black",
                                subtask.completada
                                  ? "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]"
                                  : "border-slate-200 bg-white text-slate-400",
                              ].join(" ")}
                            >
                              {subtask.completada ? "✓" : ""}
                            </span>
                            <span
                              className={[
                                "min-w-0 text-sm font-semibold",
                                subtask.completada
                                  ? "text-slate-400 line-through"
                                  : "text-slate-700",
                              ].join(" ")}
                            >
                              {subtask.titulo}
                            </span>
                          </button>

                          {capabilities.canManageSubtasks && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!window.confirm("¿Eliminar subtarea?")) return;
                                void onDeleteSubtask(task, subtask.id);
                              }}
                              className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </section>
              )}

              {task && (
                <section className="rounded-[30px] border border-[#dfe8e4] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,.06)]">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-black text-slate-950">Participantes</h3>
                    <span className="rounded-full border border-[#dfe8e4] bg-[#fbfdfc] px-3 py-1 text-xs font-black text-slate-500">
                      {task.participantes.length}
                    </span>
                  </div>

                  {capabilities.canManageParticipants && (
                    <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_auto]">
                      <select
                        value={selectedParticipant}
                        onChange={(event) => setSelectedParticipant(event.target.value)}
                        className="field-control"
                      >
                        <option value="">Selecciona usuario</option>
                        {meta.users.map((user) => (
                          <option key={user.id} value={String(user.id)}>
                            {user.nombre}
                          </option>
                        ))}
                      </select>

                      <select
                        value={selectedParticipantPermission}
                        onChange={(event) =>
                          setSelectedParticipantPermission(
                            event.target.value as TaskParticipantPermission,
                          )
                        }
                        className="field-control"
                      >
                        <option value="lector">Lector</option>
                        <option value="editor">Editor</option>
                        {capabilities.canAssign && (
                          <option value="responsable">Responsable</option>
                        )}
                      </select>

                      <button
                        type="button"
                        disabled={busy || !selectedParticipant}
                        onClick={() => {
                          void onAddParticipant(
                            task,
                            Number(selectedParticipant),
                            selectedParticipantPermission,
                          );
                          setSelectedParticipant("");
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#00d09c]/18 bg-[#00d09c]/8 px-5 py-3 text-sm font-black text-[#0b4a2f] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <UserPlus size={15} />
                        Añadir
                      </button>
                    </div>
                  )}

                  <div className="mt-4 space-y-3">
                    {task.participantes.length === 0 ? (
                      <EmptyPanel text="No hay participantes añadidos." />
                    ) : (
                      task.participantes.map((participant) => (
                        <div
                          key={participant.usuario_id}
                          className="flex items-center justify-between gap-3 rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] px-4 py-3"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-black text-slate-900">
                              {participant.usuario_nombre ?? "Usuario"}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              {participant.usuario_correo ?? "Sin email"} · {participant.permiso}
                            </div>
                          </div>

                          {capabilities.canManageParticipants && (
                            <button
                              type="button"
                              onClick={() =>
                                void onRemoveParticipant(task, participant.usuario_id)
                              }
                              className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700"
                            >
                              Quitar
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </section>
              )}

              {task && (
                <section className="rounded-[30px] border border-[#dfe8e4] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,.06)]">
                  <div className="flex items-center gap-3">
                    <MessageCircle size={18} className="text-[#0b4a2f]" />
                    <h3 className="text-lg font-black text-slate-950">Comentarios</h3>
                  </div>

                  <div className="mt-4 space-y-3">
                    {task.comentarios.length === 0 ? (
                      <EmptyPanel text="Sin comentarios todavía." />
                    ) : (
                      task.comentarios.map((item) => (
                        <div
                          key={item.id}
                          className="rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] px-4 py-3"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-black text-slate-900">
                              {item.usuario_nombre ?? "Usuario"}
                            </div>
                            <div className="text-xs font-semibold text-slate-400">
                              {formatTaskDateTime(item.creado_en)}
                            </div>
                          </div>
                          <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                            {item.comentario}
                          </p>
                        </div>
                      ))
                    )}
                  </div>

                  {capabilities.canComment && (
                    <div className="mt-4 flex flex-col gap-3">
                      <textarea
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        className="field-control min-h-[120px]"
                        placeholder="Escribe un comentario..."
                      />
                      <div className="flex justify-end">
                        <button
                          type="button"
                          disabled={busy || !comment.trim()}
                          onClick={() => {
                            void onSendComment(task, comment.trim());
                            setComment("");
                          }}
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_50%,#00a879_100%)] px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Send size={15} />
                          Enviar comentario
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}
            </div>

            <aside className="tasks-detail-scroll border-t border-white/60 bg-white/[0.34] px-5 py-5 backdrop-blur-xl lg:border-l lg:border-t-0">
              {task && (
                <div className="space-y-5">
                  <section className="rounded-[28px] border border-[#dfe8e4] bg-white p-4 shadow-[0_16px_34px_rgba(15,23,42,.05)]">
                    <div className="flex items-center gap-2">
                      <Paperclip size={16} className="text-[#0b4a2f]" />
                      <h4 className="text-base font-black text-slate-950">Adjuntos</h4>
                    </div>

                    {capabilities.canUploadAttachments && (
                      <div className="mt-4 space-y-3">
                        <input
                          type="file"
                          multiple
                          onChange={(event) =>
                            setUploadFiles(Array.from(event.target.files ?? []))
                          }
                          className="field-control"
                        />
                        <button
                          type="button"
                          disabled={busy || uploadFiles.length === 0}
                          onClick={() => {
                            void onUploadAttachments(task, uploadFiles);
                            setUploadFiles([]);
                          }}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#00d09c]/18 bg-[#00d09c]/8 px-4 py-3 text-sm font-black text-[#0b4a2f] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Upload size={15} />
                          Subir archivos
                        </button>
                      </div>
                    )}

                    <div className="mt-4 space-y-3">
                      {task.adjuntos.length === 0 ? (
                        <EmptyPanel text="Sin adjuntos por ahora." compact />
                      ) : (
                        task.adjuntos.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] px-4 py-3"
                          >
                            <div className="text-sm font-black text-slate-900">
                              {attachment.nombre_original}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              {formatTaskFileSize(attachment.tamano_bytes)} ·{" "}
                              {formatTaskDateTime(attachment.creado_en)}
                            </div>
                            <div className="mt-3 flex gap-2">
                              <button
                                type="button"
                                onClick={() => void onDownloadAttachment(attachment)}
                                className="inline-flex items-center gap-1 rounded-full border border-[#dfe8e4] bg-white px-3 py-1.5 text-xs font-black text-slate-600"
                              >
                                <Download size={13} />
                                Descargar
                              </button>

                              {capabilities.canDeleteAttachments && (
                                <button
                                  type="button"
                                  onClick={() => void onDeleteAttachment(attachment)}
                                  className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700"
                                >
                                  <Trash2 size={13} />
                                  Eliminar
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </section>

                  <section className="rounded-[28px] border border-[#dfe8e4] bg-white p-4 shadow-[0_16px_34px_rgba(15,23,42,.05)]">
                    <div className="flex items-center gap-2">
                      <Clock3 size={16} className="text-[#0b4a2f]" />
                      <h4 className="text-base font-black text-slate-950">Tiempo</h4>
                    </div>

                    {capabilities.canStartTime && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void onStartTime(task)}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_50%,#00a879_100%)] px-4 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Clock3 size={15} />
                        Iniciar fichaje
                      </button>
                    )}

                    <div className="mt-4 space-y-3">
                      {task.fichajes.length === 0 ? (
                        <EmptyPanel text="Todavía no hay sesiones de tiempo." compact />
                      ) : (
                        task.fichajes.map((entry) => (
                          <div
                            key={entry.id}
                            className="rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] px-4 py-3"
                          >
                            <div className="text-sm font-black text-slate-900">
                              {entry.usuario_nombre ?? "Usuario"}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              Inicio: {formatTaskDateTime(entry.inicio)}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              Fin: {entry.fin ? formatTaskDateTime(entry.fin) : "Abierto"}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              Duración: {formatTaskMinutes(entry.minutos)}
                            </div>

                            {entry.abierto &&
                              capabilities.canStopTime &&
                              entry.usuario_id === meta.current_user.id && (
                                <button
                                  type="button"
                                  onClick={() => void onStopTime(task, entry)}
                                  className="mt-3 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-black text-amber-700"
                                >
                                  Detener
                                </button>
                              )}
                          </div>
                        ))
                      )}
                    </div>
                  </section>

                  <section className="rounded-[28px] border border-[#dfe8e4] bg-white p-4 shadow-[0_16px_34px_rgba(15,23,42,.05)]">
                    <h4 className="text-base font-black text-slate-950">Actividad</h4>
                    <div className="mt-4 space-y-3">
                      {task.actividad.length === 0 ? (
                        <EmptyPanel text="Sin actividad registrada." compact />
                      ) : (
                        task.actividad.map((item) => (
                          <div
                            key={item.id}
                            className="rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] px-4 py-3"
                          >
                            <div className="text-sm font-black text-slate-900">
                              {item.usuario_nombre ?? "Sistema"}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              {item.accion}
                              {item.campo ? ` · ${item.campo}` : ""}
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-400">
                              {formatTaskDateTime(item.creado_en)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </section>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}


function getTaskStateActionLabel(state: TaskState): string {
  const labels: Record<TaskState, string> = {
    pendiente: "Devolver a pendiente",
    progreso: "Iniciar trabajo",
    revision: "Enviar a revisión",
    completado: "Aceptar y finalizar",
  };

  return labels[state] ?? `Mover a ${getTaskStateLabel(state)}`;
}

function ActionButton({
  icon: Icon,
  text,
  busy,
  tone = "default",
  onClick,
}: {
  icon: typeof CheckCircle2;
  text: string;
  busy: boolean;
  tone?: "default" | "danger";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={[
        "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-black shadow-[0_10px_26px_rgba(15,23,42,.055)] transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60",
        tone === "danger"
          ? "border border-rose-200 bg-rose-50 text-rose-700"
          : "border border-[#dfe8e4] bg-white text-slate-700",
      ].join(" ")}
    >
      <Icon size={15} />
      {text}
    </button>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[22px] border border-[#edf1f3] bg-[#fbfdfc] p-4">
      <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 text-sm font-black text-slate-900">{value}</div>
    </div>
  );
}

function EmptyPanel({ text, compact = false }: { text: string; compact?: boolean }) {
  return (
    <div
      className={[
        "rounded-[22px] border border-dashed border-[#dfe8e4] bg-[#fbfdfc] text-sm font-semibold text-slate-400",
        compact ? "px-4 py-4" : "px-4 py-5",
      ].join(" ")}
    >
      {text}
    </div>
  );
}
